/**
 * Quantity Price Update
 * Extends the theme's existing price update system to show total prices
 * Uses the theme's Shopify.formatMoney function and hooks into existing _updatePrice
 */

class QuantityPriceUpdate {
  constructor() {
    this.currentVariant = null;
    this.init();
  }

  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.bindEvents());
    } else {
      this.bindEvents();
    }
  }

  bindEvents() {
    const quantityInputs = document.querySelectorAll('.quantity-field[name="quantity"]');
    
    quantityInputs.forEach(input => {
      input.addEventListener('input', (e) => this.updateTotalPrice(e.target));
      input.addEventListener('change', (e) => this.updateTotalPrice(e.target));
      
      setTimeout(() => {
        this.ensurePriceVisibility();
        this.updateTotalPrice(input);
      }, 100);
    });

    this.hookIntoThemePriceUpdates();
    
    this.setupVisibilityWatcher();
  }

  updateTotalPrice(quantityInput) {
    const quantity = parseInt(quantityInput.value) || 1;
    const productForm = quantityInput.closest('[data-product-form]');
    
    if (!productForm) return;

    this.updateMetersDisplay(quantityInput, quantity);

    const currentVariant = this.getCurrentVariant();
    if (!currentVariant) return;

    const totalPriceElement = productForm.querySelector('[data-product-total-price]');
    const totalComparePriceElement = productForm.querySelector('[data-product-total-price-compare]');
    const totalPriceContainer = productForm.querySelector('[data-total-price-container]');

    if (!totalPriceElement || !totalPriceContainer) return;

    if (window.Shopify && window.Shopify.formatMoney && window.Theme && window.Theme.moneyFormat) {
      const totalPrice = currentVariant.price * quantity;
      const totalComparePrice = currentVariant.compare_at_price * quantity;

      totalPriceElement.innerHTML = Shopify.formatMoney(totalPrice, window.Theme.moneyFormat);
      
      if (totalComparePriceElement && currentVariant.compare_at_price > currentVariant.price) {
        totalComparePriceElement.innerHTML = Shopify.formatMoney(totalComparePrice, window.Theme.moneyFormat);
        totalComparePriceElement.classList.remove('product-price__total-compare--hidden');
        totalPriceElement.classList.add('product-price__total--sale');
      } else if (totalComparePriceElement) {
        totalComparePriceElement.innerHTML = '';
        totalComparePriceElement.classList.add('product-price__total-compare--hidden');
        totalPriceElement.classList.remove('product-price__total--sale');
      }

    }

    if (quantity > 1) {
      totalPriceContainer.style.display = 'block';
    } else {
      totalPriceContainer.style.display = 'none';
    }

    const individualPriceContainer = productForm.querySelector('.product-price:not(.product-price--total)');
    if (individualPriceContainer) {
      individualPriceContainer.style.display = 'block';
      individualPriceContainer.style.visibility = 'visible';
    }

    const unitPriceElement = productForm.querySelector('[data-product-price]');
    if (unitPriceElement) {
      unitPriceElement.style.display = '';
      unitPriceElement.style.visibility = 'visible';
    }
  }

  updateMetersDisplay(quantityInput, quantity) {
    if (!quantityInput.hasAttribute('data-minimum-quantity-enabled')) return;

    const metersValueElement = document.querySelector('[data-meters-value]');
    if (!metersValueElement) return;

    const meters = (quantity * 0.1).toFixed(1);
    metersValueElement.textContent = meters;
    
    const form = quantityInput.closest('form');
    if (form) {
      const meterProperty = form.querySelector('[data-meter-property]');
      if (meterProperty) {
        const meterUnit = form.querySelector('[data-quantity-meters]')?.textContent?.match(/\s(\w+)$/)?.[1] || 'meter';
        meterProperty.value = `${meters} ${meterUnit}`;
      }
    }
  }

  getCurrentVariant() {
    if (this.currentVariant) {
      return this.currentVariant;
    }

    const variantSelect = document.querySelector('[name="id"]');
    if (variantSelect && variantSelect.value) {
      if (window.productData && window.productData.variants) {
        const variant = window.productData.variants.find(v => v.id == variantSelect.value);
        if (variant) {
          this.currentVariant = variant;
          return variant;
        }
      }
    }

    const priceElement = document.querySelector('[data-product-price]');
    if (priceElement && priceElement.dataset.unitPrice) {
      return {
        price: parseInt(priceElement.dataset.unitPrice),
        compare_at_price: parseInt(document.querySelector('[data-product-price-compare]')?.dataset.unitComparePrice || 0)
      };
    }

    return null;
  }

  ensurePriceVisibility() {
    const productForm = document.querySelector('[data-product-form]');
    if (!productForm) return;

    const priceContainer = productForm.querySelector('.product-details__price-container');
    const individualPriceContainer = productForm.querySelector('.product-price:not(.product-price--total)');
    const unitPriceElement = productForm.querySelector('[data-product-price]');
    const comparePriceElement = productForm.querySelector('[data-product-price-compare]');

    if (priceContainer) {
      priceContainer.style.display = 'block';
      priceContainer.style.visibility = 'visible';
      priceContainer.style.opacity = '1';
    }

    if (individualPriceContainer) {
      individualPriceContainer.style.display = 'block';
      individualPriceContainer.style.visibility = 'visible';
      individualPriceContainer.style.opacity = '1';
      individualPriceContainer.style.position = 'relative';
      individualPriceContainer.style.zIndex = '1';
    }

    if (unitPriceElement) {
      unitPriceElement.style.display = 'inline';
      unitPriceElement.style.visibility = 'visible';
      unitPriceElement.style.opacity = '1';
      
      unitPriceElement.classList.remove('hidden');
      unitPriceElement.classList.remove('product-price--hidden');
    }

    if (comparePriceElement) {
      if (!comparePriceElement.classList.contains('product-price__compare--hidden')) {
        comparePriceElement.style.display = 'inline';
        comparePriceElement.style.visibility = 'visible';
      }
    }

  }

  setupVisibilityWatcher() {
    const priceContainer = document.querySelector('.product-details__price-container');
    if (!priceContainer) return;

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && 
            (mutation.attributeName === 'style' || mutation.attributeName === 'class')) {
          const target = mutation.target;
          if (target.matches('.product-price:not(.product-price--total)') || 
              target.matches('[data-product-price]')) {
            
                         const computedStyle = window.getComputedStyle(target);
             if (computedStyle.display === 'none' || computedStyle.visibility === 'hidden') {
               this.ensurePriceVisibility();
             }
          }
        }
      });
    });

    observer.observe(priceContainer, {
      attributes: true,
      attributeFilter: ['style', 'class'],
      childList: true,
      subtree: true
    });

    setInterval(() => {
             const unitPriceElement = document.querySelector('[data-product-price]');
       if (unitPriceElement) {
         const computedStyle = window.getComputedStyle(unitPriceElement);
         if (computedStyle.display === 'none' || computedStyle.visibility === 'hidden') {
           this.ensurePriceVisibility();
         }
       }
    }, 2000); 
  }

  hookIntoThemePriceUpdates() {
    const priceElement = document.querySelector('[data-product-price]');
    if (!priceElement) return;

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' || mutation.type === 'characterData') {
          this.currentVariant = null;
          
          const quantityInput = document.querySelector('.quantity-field[name="quantity"]');
          if (quantityInput) {
            setTimeout(() => {
              this.ensurePriceVisibility();
              this.updateTotalPrice(quantityInput);
            }, 50);
          }
        }
      });
    });

    observer.observe(priceElement, {
      childList: true,
      characterData: true,
      subtree: true
    });

    const comparePriceElement = document.querySelector('[data-product-price-compare]');
    if (comparePriceElement) {
      observer.observe(comparePriceElement, {
        childList: true,
        characterData: true,
        subtree: true
      });
    }
  }


}

window.quantityPriceUpdateInstance = new QuantityPriceUpdate(); 